<template>
  <div class="container-fluid">
    <loading :active.sync="isLoading" :is-full-page="false"></loading>
    <div class="row">
      <div class="col-12">
        <div class="page-title-box d-flex align-items-center justify-content-between">
          <h4 class="mb-0 font-size-18">Detail {{ module }}</h4>
          <div class="page-title-right">
            <ol class="breadcrumb m-0">
              <li class="breadcrumb-item"><router-link :to="{name:'dashboards'}">Dashboard</router-link></li>
              <li class="breadcrumb-item"><router-link :to="{name:'product_orders'}">{{ module }}</router-link></li>
              <li class="breadcrumb-item active">Detail</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="order">
      <div class="col-lg-6">
        <div class="card shadow-sm mb-4">
          <div class="card-body">
            <h5 class="card-title">Informasi Pesanan</h5>
            <table class="table">
              <tr><td>Kode</td><td><strong>{{ order.po_code }}</strong></td></tr>
              <tr><td>Status</td><td><span :class="statusBadge(order.po_status)">{{ statusLabel(order.po_status) }}</span></td></tr>
              <tr><td>Total Produk</td><td><strong>Rp {{ formatNumber(order.po_total) }}</strong></td></tr>
              <tr v-if="order.shipping_cost > 0"><td>Ongkos Kirim</td><td><strong>Rp {{ formatNumber(order.shipping_cost) }}</strong></td></tr>
              <tr v-if="order.shipping_cost > 0 || order.po_status >= 1"><td>Grand Total</td><td><strong>Rp {{ formatNumber(order.po_total + order.shipping_cost) }}</strong></td></tr>
              <tr><td>Tanggal</td><td>{{ formatDate(order.created_at) }}</td></tr>
              <tr v-if="order.admin_confirmed_at"><td>Dikonfirmasi Admin</td><td>{{ formatDate(order.admin_confirmed_at) }}</td></tr>
              <tr v-if="order.confirmed_at && order.confirmed_at !== order.admin_confirmed_at"><td>Pembayaran Dikonfirmasi</td><td>{{ formatDate(order.confirmed_at) }}</td></tr>
              <tr><td>Metode Ongkir</td>
                <td>
                  <span class="badge badge-primary" v-if="order.shipping_method === 'manual'">Bayar Manual Ongkir</span>
                  <span class="badge badge-success" v-else-if="order.shipping_method === 'cod'">COD (Bayar di Tempat)</span>
                  <span class="badge badge-info" v-else-if="order.shipping_method === 'free'">Gratis Ongkir</span>
                  <span class="badge badge-secondary" v-else>{{ order.shipping_method }}</span>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="card shadow-sm mb-4" v-if="order.po_status === 3 && order.resi_number">
          <div class="card-body">
            <h5 class="card-title">Cetak</h5>
            <div class="d-flex" style="gap: 8px;">
              <a :href="printResiUrl" target="_blank" class="btn btn-primary">
                <i class="bx bx-printer"></i> Resi
              </a>
              <a :href="printReceiptUrl" target="_blank" class="btn btn-success">
                <i class="bx bx-receipt"></i> Struk Pembelian
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="card shadow-sm mb-4" v-if="order.customer">
          <div class="card-body">
            <h5 class="card-title">Informasi Customer</h5>
            <table class="table">
              <tr><td>Nama</td><td><strong>{{ order.customer.usr_fullname }}</strong></td></tr>
              <tr v-if="order.customer.usr_phone"><td>Telepon</td><td>{{ order.customer.usr_phone }}</td></tr>
              <tr v-if="order.customer.usr_email"><td>Email</td><td>{{ order.customer.usr_email }}</td></tr>
            </table>
          </div>
        </div>

        <div class="card shadow-sm mb-4" v-if="order.address">
          <div class="card-body">
            <h5 class="card-title">Alamat Pengiriman</h5>
            <table class="table">
              <tr><td>Label</td><td><strong>{{ order.address.label }}</strong></td></tr>
              <tr><td>Penerima</td><td>{{ order.address.recipient_name }}</td></tr>
              <tr v-if="order.address.phone"><td>Telepon</td><td>{{ order.address.phone }}</td></tr>
              <tr><td>Alamat</td><td>{{ order.address.address }}, {{ order.address.city }}, {{ order.address.province }} {{ order.address.postal_code }}</td></tr>
            </table>
          </div>
        </div>

        <div class="card shadow-sm mb-4" v-if="order.po_status === 0 && order.shipping_method === 'manual'">
          <div class="card-body">
            <h5 class="card-title">Konfirmasi Pesanan</h5>
            <p>Pesanan ini menunggu konfirmasi admin untuk menentukan ongkos kirim.</p>
            <div class="form-group">
              <label>Ongkos Kirim (perkiraan) <span class="text-danger">*</span></label>
              <input type="number" class="form-control" v-model="formShippingCost" placeholder="Masukkan biaya ongkos kirim" min="0" />
            </div>
            <button class="btn btn-success" @click="confirmOrder">
              <i class="bx bx-check"></i> Konfirmasi Pesanan
            </button>
          </div>
        </div>

        <div class="card shadow-sm mb-4" v-if="order.proof_image">
          <div class="card-body">
            <h5 class="card-title">Bukti Transfer</h5>
            <img :src="$root.backEnd + order.proof_image" class="img-fluid" style="max-height: 300px; cursor: pointer;" @click="previewImage = $root.backEnd + order.proof_image; showPreview = true" />
            <p v-if="order.proof_note" class="mt-2"><strong>Catatan:</strong> {{ order.proof_note }}</p>
            <div v-if="order.po_status === 2" class="mt-2 d-flex" style="gap: 8px;">
              <button class="btn btn-success" @click="showConfirmPaymentModal = true">Konfirmasi Pembayaran</button>
              <button class="btn btn-danger" @click="rejectProof">Tolak</button>
            </div>
          </div>
        </div>

        <div class="card shadow-sm mb-4" v-if="order.resi_number">
          <div class="card-body">
            <h5 class="card-title">Informasi Pengiriman</h5>
            <table class="table">
              <tr><td>No. Resi</td><td><strong>{{ order.resi_number }}</strong></td></tr>
              <tr v-if="order.shipping_cost > 0"><td>Ongkos Kirim</td><td><strong>Rp {{ formatNumber(order.shipping_cost) }}</strong></td></tr>
              <tr v-if="order.shipping_method === 'cod' && order.shipping_cost > 0">
                <td>Catatan</td>
                <td><em>Ongkir dibayar saat COD</em></td>
              </tr>
              <tr v-if="order.resi_proof_image">
                <td>Bukti Kirim</td>
                <td>
                  <img :src="$root.backEnd + order.resi_proof_image" style="max-height: 150px; cursor: pointer;" @click="previewImage = $root.backEnd + order.resi_proof_image; showPreview = true" />
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-body">
            <h5 class="card-title">Item Pesanan</h5>
            <table class="table">
              <thead>
                <tr><th>Produk</th><th>Harga</th><th>Qty</th><th>Subtotal</th></tr>
              </thead>
              <tbody>
                <tr v-for="item in order.items" :key="item.poi_id">
                  <td>{{ item.prd_name }}</td>
                  <td>Rp {{ formatNumber(item.prd_price) }}</td>
                  <td>{{ item.qty }}</td>
                  <td>Rp {{ formatNumber(item.subtotal) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <b-modal v-model="showPreview" size="lg" title="Preview" hide-footer>
      <img :src="previewImage" class="img-fluid" />
    </b-modal>

    <b-modal v-model="showConfirmPaymentModal" title="Konfirmasi Pembayaran" @ok="confirmPayment" ok-title="Konfirmasi" ok-variant="success">
      <div class="form-group">
        <label>Nomor Resi <span class="text-danger">*</span></label>
        <input type="text" class="form-control" v-model="formResiNumber" placeholder="Masukkan nomor resi pengiriman" />
      </div>
      <div class="form-group" v-if="order && order.shipping_method === 'cod'">
        <label>Ongkos Kirim (untuk disiapkan user saat COD) <span class="text-danger">*</span></label>
        <input type="number" class="form-control" v-model="formShippingCost" placeholder="Masukkan biaya ongkos kirim" min="0" />
      </div>
      <div class="form-group">
        <label>Bukti Pengiriman (opsional)</label>
        <input type="file" class="form-control-file" accept="image/*" @change="onResiProofChange" />
        <small class="text-muted">Format: JPEG/PNG, max 2MB</small>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
export default {
  components: { Loading },
  data() {
    return {
      module: 'Product Orders',
      table_module: 'product_orders',
      order: null,
      isLoading: true,
      token: localStorage.getItem('token') || '',
      previewImage: '',
      showPreview: false,
      showConfirmPaymentModal: false,
      formShippingCost: 0,
      formResiNumber: '',
      formResiProof: null,
    };
  },
  computed: {
    printResiUrl() {
      return this.$root.backEnd + 'v1/product-purchase/admin/orders/' + (this.order?.po_uuid || '') + '/print-resi?token=' + this.token;
    },
    printReceiptUrl() {
      return this.$root.backEnd + 'v1/product-purchase/admin/orders/' + (this.order?.po_uuid || '') + '/print-receipt?token=' + this.token;
    },
  },
  mounted() {
    this.getDetail();
  },
  methods: {
    getDetail() {
      const uuid = this.$route.params.uuid;
      this.axios.get(`/product-purchase/admin/orders/${uuid}`)
        .then(response => {
          this.isLoading = false;
          if (response.data.status === 200) {
            this.order = response.data.data;
            this.formShippingCost = response.data.data.shipping_cost || 0;
          }
        })
        .catch(() => { this.isLoading = false; });
    },
    onResiProofChange(e) {
      this.formResiProof = e.target.files?.[0] || null;
    },
    confirmOrder() {
      if (!this.formShippingCost && this.formShippingCost !== 0) {
        this.$swal.fire('Error', 'Ongkos kirim wajib diisi', 'error');
        return;
      }
      this.$swal.fire({
        title: 'Konfirmasi Pesanan?',
        text: 'Ongkir akan ditetapkan sebesar Rp ' + this.formatNumber(this.formShippingCost),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya, Konfirmasi',
        cancelButtonText: 'Batal',
        confirmButtonColor: '#28a745',
      }).then(result => {
        if (result.value) {
          this.axios.post(`/product-purchase/admin/orders/${this.order.po_uuid}/confirm-order`, { shipping_cost: this.formShippingCost })
            .then(response => {
              if (response.data.status === 200) {
                this.$swal.fire('Berhasil', 'Pesanan dikonfirmasi dengan ongkir Rp ' + this.formatNumber(this.formShippingCost), 'success');
                this.getDetail();
              } else {
                this.$swal.fire('Gagal', response.data.message, 'error');
              }
            })
            .catch(() => {
              this.$swal.fire('Error', 'Gagal konfirmasi pesanan', 'error');
            });
        }
      });
    },
    rejectProof() {
      this.$swal.fire({
        title: 'Tolak Bukti Transfer?',
        text: 'Status pesanan akan dikembalikan ke "Menunggu Pembayaran"',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, Tolak',
        cancelButtonText: 'Batal',
        confirmButtonColor: '#dc3545',
      }).then(result => {
        if (result.value) {
          this.axios.post(`/product-purchase/admin/orders/${this.order.po_uuid}/reject-proof`)
            .then(response => {
              if (response.data.status === 200) {
                this.$swal.fire('Berhasil', 'Bukti transfer ditolak', 'success');
                this.getDetail();
              } else {
                this.$swal.fire('Gagal', response.data.message, 'error');
              }
            })
            .catch(() => {
              this.$swal.fire('Error', 'Gagal menolak bukti', 'error');
            });
        }
      });
    },
    confirmPayment(e) {
      if (!this.formResiNumber) {
        this.$swal.fire('Error', 'Nomor resi wajib diisi', 'error');
        e.preventDefault();
        return;
      }

      const formData = new FormData();
      formData.append('resi_number', this.formResiNumber);
      if (this.order.shipping_method === 'cod') {
        if (!this.formShippingCost && this.formShippingCost !== 0) {
          this.$swal.fire('Error', 'Ongkos kirim wajib diisi untuk COD', 'error');
          e.preventDefault();
          return;
        }
        formData.append('shipping_cost', this.formShippingCost);
      }
      if (this.formResiProof) {
        formData.append('resi_proof_image', this.formResiProof);
      }

      this.axios.post(`/product-purchase/admin/orders/${this.order.po_uuid}/confirm`, formData)
        .then(response => {
          if (response.data.status === 200) {
            this.showConfirmPaymentModal = false;
            this.formResiNumber = '';
            this.formShippingCost = 0;
            this.formResiProof = null;
            this.$swal.fire('Berhasil', 'Pembayaran dikonfirmasi', 'success');
            this.getDetail();
          } else {
            this.$swal.fire('Gagal', response.data.message, 'error');
          }
        })
        .catch(() => {
          this.$swal.fire('Error', 'Gagal konfirmasi pembayaran', 'error');
        });
    },
    statusLabel(status) {
      const labels = { 0: 'Menunggu Konfirmasi Admin', 1: 'Menunggu Pembayaran', 2: 'Bukti Diupload', 3: 'Dikonfirmasi', 9: 'Dibatalkan' };
      return labels[status] || 'Unknown';
    },
    statusBadge(status) {
      const classes = { 0: 'badge badge-warning', 1: 'badge badge-primary', 2: 'badge badge-info', 3: 'badge badge-success', 9: 'badge badge-danger' };
      return classes[status] || 'badge badge-secondary';
    },
    formatNumber(num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const opts = { timeZone: 'Asia/Jakarta', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false };
      return d.toLocaleString('id-ID', opts).replace(/(\d+)\/(\d+)\/(\d+)/, '$1-$2-$3').replace(/\./g, ':');
    },
  }
};
</script>
